import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, Injectable, signal } from '@angular/core';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'admin-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly media = this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)');

  private readonly systemDark = signal(this.media?.matches ?? false);
  private readonly _preference = signal<ThemePreference>(this.readStored());

  readonly preference = this._preference.asReadonly();
  readonly resolvedTheme = computed<ResolvedTheme>(() => {
    const pref = this._preference();
    return pref === 'system' ? (this.systemDark() ? 'dark' : 'light') : pref;
  });

  constructor() {
    this.media?.addEventListener?.('change', e => this.systemDark.set(e.matches));
    effect(() => this.applyTheme(this.resolvedTheme()));
    effect(() => this.persist(this._preference()));
  }

  setPreference(preference: ThemePreference): void {
    this._preference.set(preference);
  }

  toggle(): void {
    this._preference.set(this.resolvedTheme() === 'dark' ? 'light' : 'dark');
  }

  private applyTheme(theme: ResolvedTheme): void {
    const root = this.document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    root.style.colorScheme = theme;
  }

  private persist(preference: ThemePreference): void {
    try {
      if (preference === 'system') {
        this.document.defaultView?.localStorage?.removeItem(STORAGE_KEY);
      } else {
        this.document.defaultView?.localStorage?.setItem(STORAGE_KEY, preference);
      }
    } catch {
      /* storage unavailable (private mode / SSR) — preference stays in-memory */
    }
  }

  private readStored(): ThemePreference {
    try {
      const stored = this.document.defaultView?.localStorage?.getItem(STORAGE_KEY);
      return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'dark';
    } catch {
      return 'dark';
    }
  }
}
